import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faArrowTurnDownLeft, faCheck, faCopy, faSave } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { SnippetFormMode } from "../hooks/useAiSnippetForm";

interface SnippetModalFooterProps {
	mode: SnippetFormMode;
	canSubmit: boolean;
	isLoading: boolean;
	onCancel: () => void;
	onDiscardChanges: () => void;
	onSaveAsNew: () => void;
	onUpdate: () => void;
	onInsertExisting: () => void;
	onInsertEphemeral: () => void;
	onSaveAsTemplateAndInsert: () => void;
}

// The shared Button has no borderless "tertiary" variant, so de-emphasize the
// secondary style instead.
const TERTIARY_CLASSES =
	"!border-transparent !bg-transparent !text-text-meta hover:!bg-background-light hover:!text-text hover:!shadow-none";

export const SnippetModalFooter: FunctionComponent<SnippetModalFooterProps> = ({
	mode,
	canSubmit,
	isLoading,
	onCancel,
	onDiscardChanges,
	onSaveAsNew,
	onUpdate,
	onInsertExisting,
	onInsertEphemeral,
	onSaveAsTemplateAndInsert,
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex-shrink-0 px-6 py-3.5 border-t border-border-light bg-white flex items-center justify-between gap-3">
			<div className="text-[11.5px] text-text-meta flex items-center gap-1.5" />

			<div className="flex items-center gap-2">
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					size={ButtonSize.S}
					title={t("aiSnippet.modal.footer.cancel")}
					type="button"
					onClick={onCancel}
				/>

				{mode === "existing" && (
					<Button
						color={ButtonColor.BRAND}
						disabled={isLoading}
						image={<Icon className="w-3 h-3 mr-1.5" icon={faArrowTurnDownLeft} />}
						size={ButtonSize.S}
						title={t("aiSnippet.modal.footer.insertExisting")}
						type="button"
						onClick={onInsertExisting}
					/>
				)}

				{mode === "modified" && (
					<>
						<Button
							className={TERTIARY_CLASSES}
							color={ButtonColor.ACTION_SECONDARY}
							disabled={isLoading}
							size={ButtonSize.S}
							title={t("aiSnippet.modal.footer.discardChanges")}
							type="button"
							onClick={onDiscardChanges}
						/>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={!canSubmit || isLoading}
							image={<Icon className="w-3 h-3 mr-1.5" icon={faCopy} />}
							size={ButtonSize.S}
							title={t("aiSnippet.modal.footer.saveAsNew")}
							type="button"
							onClick={onSaveAsNew}
						/>
						<Button
							color={ButtonColor.BRAND}
							disabled={!canSubmit || isLoading}
							image={<Icon className="w-3 h-3 mr-1.5" icon={faCheck} />}
							size={ButtonSize.S}
							title={t("aiSnippet.modal.footer.update")}
							type="button"
							onClick={onUpdate}
						/>
					</>
				)}

				{mode === "new" && (
					<>
						<Tooltip content={t("aiSnippet.modal.footer.insertEphemeralHint")}>
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								disabled={!canSubmit || isLoading}
								image={<Icon className="w-3 h-3 mr-1.5" icon={faArrowTurnDownLeft} />}
								size={ButtonSize.S}
								title={t("aiSnippet.modal.footer.insertEphemeral")}
								type="button"
								onClick={onInsertEphemeral}
							/>
						</Tooltip>
						<Button
							color={ButtonColor.BRAND}
							disabled={!canSubmit || isLoading}
							image={<Icon className="w-3 h-3 mr-1.5" icon={faSave} />}
							size={ButtonSize.S}
							title={t("aiSnippet.modal.footer.saveAsTemplateAndInsert")}
							type="button"
							onClick={onSaveAsTemplateAndInsert}
						/>
					</>
				)}
			</div>
		</div>
	);
};
